import { Autocomplete, MenuItem, TextField } from '@mui/material';
import { athleteDisplayName, AthletePreview } from '@strength/domain';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorIcon } from '../icons/error-icon';

export function AthleteSelect({
  athletes,
  selectedAthlete,
  onChange,
  hasError,
  fullWidth,
}: {
  athletes: AthletePreview[];
  selectedAthlete: AthletePreview | null;
  onChange: (selectedId: string | null) => void;
  hasError?: (profileId: string) => boolean;
  fullWidth?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Autocomplete
      options={athletes}
      value={selectedAthlete}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      forcePopupIcon
      fullWidth={fullWidth}
      getOptionLabel={(option) => athleteDisplayName(option)}
      onChange={(_e, value) => {
        if (!value) {
          onChange(null);
        } else {
          onChange(value.id ?? null);
        }
      }}
      renderOption={(props, option) => (
        <AthleteSelectItem
          value={option.id}
          {...props}
          athlete={option}
          hasError={hasError}
        />
      )}
      renderInput={(params) => (
        <TextField {...params} placeholder={t('allAthletes')} sx={{ mt: 0 }} />
      )}
      sx={{
        width: fullWidth ? '100%' : '200px',
      }}
    />
  );
}

interface AthleteSelectItemProps {
  athlete: AthletePreview;
  value: string;
  hasError?: (profileId: string) => boolean;
}

// forwardRef is needed on custom MenuItems for Select to work properly
// https://stackoverflow.com/questions/68378474/mui-select-custom-menuitem-not-working-properly
const AthleteSelectItem = forwardRef<HTMLLIElement, AthleteSelectItemProps>(
  ({ athlete, hasError, ...rest }, ref) => {
    return (
      <MenuItem {...rest} ref={ref}>
        <div className="flex items-center">
          <span>{athleteDisplayName(athlete)}</span>
          {hasError?.(athlete.id) && <ErrorIcon className="ml-2" />}
        </div>
      </MenuItem>
    );
  },
);
